import { makeObservable, observable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import {
  convertAntibioticsToCalendarEvents,
  convertEQ5D5LFormsToCalendarEvents,
  convertImmunoglobulinFormsToCalendarEvents,
  convertMyNotesToCalendarEvents,
  convertSickDayFormsToCalendarEvents,
  convertSymptomsToCalendarEvents,
  convertVaccinesToCalendarEvents,
} from "../utils/calendarHelpers";
import { CalendarEvent } from "../models/Calendar";
import { FieldGroupId } from "../lib/enums";

class CalendarStore {
  rootStore: RootStore;
  calendarEvents: CalendarEvent[] | undefined = undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      calendarEvents: observable,
    });
  }

  setCalendarEvents(newCalendarEvents: CalendarEvent[] | undefined) {
    runInAction(() => {
      this.calendarEvents = newCalendarEvents ?? [];
    });
  }

  getCalendarEvents = async () => {
    let events = [] as CalendarEvent[];
    if (!this.rootStore.measurementStore.daySymptoms) {
      await this.rootStore.measurementStore.getSymptoms();
    }
    if (!this.rootStore.treatmentStore.treatments) {
      await this.rootStore.treatmentStore.getTreatments();
    }
    if (!this.rootStore.measurementStore.vaccinations) {
      await this.rootStore.measurementStore.getVaccinations();
    }
    if (!this.rootStore.measurementStore.notes) {
      await this.rootStore.measurementStore.getNotes();
    }
    if (!this.rootStore.formStore.forms) {
      await this.rootStore.formStore.getForms();
    }
    if (
      !this.rootStore.fieldStore.fields ||
      !this.rootStore.fieldStore.mappedSideEffects ||
      !this.rootStore.fieldStore.mappedSymptomNames ||
      !this.rootStore.fieldStore.mappedVaccines
    ) {
      await this.rootStore.fieldStore.getFields();
    }
    if (
      !this.rootStore.drugStore.drugs ||
      !this.rootStore.drugStore.mappedDrugs
    ) {
      await this.rootStore.drugStore.getDrugs();
    }
    let symptomevents = [] as CalendarEvent[];
    let treatmentevents = [] as CalendarEvent[];
    let vaccinationevents = [] as CalendarEvent[];
    let eq5d5lformevents = [] as CalendarEvent[];
    let immunoglobulinformevents = [] as CalendarEvent[];
    let sickdaysformevents = [] as CalendarEvent[];
    let caredaysformevents = [] as CalendarEvent[];
    let mynotesevents = [] as CalendarEvent[];

    if (
      this.rootStore.measurementStore.daySymptoms &&
      this.rootStore.fieldStore.mappedSymptomNames
    ) {
      const daySymptoms = this.rootStore.measurementStore.daySymptoms;
      const mappedSymptomNames = this.rootStore.fieldStore.mappedSymptomNames;

      const symptoms = convertSymptomsToCalendarEvents(
        daySymptoms,
        mappedSymptomNames
      );
      symptomevents = symptoms;
    }
    if (
      this.rootStore.treatmentStore.treatments &&
      this.rootStore.drugStore.mappedDrugs
    ) {
      const treatments = this.rootStore.treatmentStore.treatments;
      const mappedDrugs = this.rootStore.drugStore.mappedDrugs;

      const treatmentEvents = convertAntibioticsToCalendarEvents(
        treatments,
        mappedDrugs
      );
      if (treatmentEvents) {
        treatmentevents = treatmentEvents;
      }
    }
    if (
      this.rootStore.measurementStore.vaccinations &&
      this.rootStore.fieldStore.mappedVaccines
    ) {
      const vaccinations = this.rootStore.measurementStore.vaccinations;
      const mappedVaccines = this.rootStore.fieldStore.mappedVaccines;

      const vaccinationEvents = convertVaccinesToCalendarEvents(
        vaccinations,
        mappedVaccines
      );
      if (vaccinationEvents) {
        vaccinationevents = vaccinationEvents;
      }
    }
    if (this.rootStore.measurementStore.notes) {
      const notes = this.rootStore.measurementStore.notes;
      const myNotesEvents = convertMyNotesToCalendarEvents(notes);

      if (myNotesEvents) {
        mynotesevents = myNotesEvents;
      }
    }
    if (
      this.rootStore.formStore.forms &&
      this.rootStore.fieldStore.mappedSideEffects &&
      this.rootStore.fieldStore.mappedDrugs
    ) {
      const eq5d5lForms = await this.rootStore.formStore.getFormsByFormId(
        FieldGroupId.EQ5D5L_FIELD_GROUP_ID
      );
      const immunoglobulinForms =
        await this.rootStore.formStore.getFormsByFormId(
          FieldGroupId.IMMUNOGLOBULIN_FIELD_GROUP_ID
        );
      const sickDaysForms = await this.rootStore.formStore.getFormsByFormId(
        FieldGroupId.SICKDAYS_FIELD_GROUP_ID
      );

      const careDaysForms = await this.rootStore.formStore.getFormsByFormId(
        FieldGroupId.CAREDAYS_FIELD_GROUP_ID
      );
      const mappedSideEffects = this.rootStore.fieldStore.mappedSideEffects;
      if (eq5d5lForms) {
        const eq5d5lEvents = convertEQ5D5LFormsToCalendarEvents(eq5d5lForms);
        if (eq5d5lEvents) {
          eq5d5lformevents = eq5d5lEvents;
        }
      }
      if (immunoglobulinForms) {
        if (mappedSideEffects) {
          const immunoglobulinEvents =
            convertImmunoglobulinFormsToCalendarEvents(
              immunoglobulinForms,
              mappedSideEffects
            );
          if (immunoglobulinEvents) {
            immunoglobulinformevents = immunoglobulinEvents;
          }
        }
      }
      if (sickDaysForms) {
        const sickDaysEvents =
          convertSickDayFormsToCalendarEvents(sickDaysForms);
        if (sickDaysEvents) {
          sickdaysformevents = sickDaysEvents;
        }
      }
      if (careDaysForms) {
        const careDaysEvents =
          convertSickDayFormsToCalendarEvents(careDaysForms);
        if (careDaysEvents) {
          caredaysformevents = careDaysEvents;
        }
      }
    }
    events = [
      ...symptomevents,
      ...treatmentevents,
      ...vaccinationevents,
      ...sickdaysformevents,
      ...caredaysformevents,
      ...mynotesevents,
      ...eq5d5lformevents,
      ...immunoglobulinformevents,
    ];
    this.setCalendarEvents(events);
  };
}

export default CalendarStore;
